import { z } from "zod";
import {
  createGetListRequestSchema,
  DomainModelMetaExtendedSchema,
  DomainModelMetaSchema,
  DomainModelSchema,
  type TypeOfObjectValues,
} from "../util/index.ts";
import { AssetTypeSchema } from "./AssetSchemas.ts";
import {
  SystemActionType,
  UserTaskType,
  WorkflowStepType,
  WorkflowTaskPriorityLabelSchema,
} from "./WorkflowConfigSchemas.ts";
import { WorkflowSchema, WorkflowStatus } from "./WorkflowSchemas.ts";
import { WorkflowTemplateStatus } from "./WorkflowTemplateSchemas.ts";

export const WorkflowTaskNotificationEventType = {
  CREATED: "created",
  STATUS_CHANGED: "status_changed",
  ASSIGNEE_CHANGED: "assignee_changed",
} as const;

export const WorkflowTaskNotificationRecipientType = {
  WORKFLOW_OWNER: "workflow_owner",
  ASSIGNEE: "assignee",
  ASSIGNEE_USER: "assignee_user",
  ASSIGNEE_GROUP: "assignee_group",
  USERS: "users",
} as const;

export const WorkflowTaskTypes = {
  ...SystemActionType,
  ...UserTaskType,
} as const;
export type WorkflowTaskTypesType = TypeOfObjectValues<
  typeof WorkflowTaskTypes
>;

export const WorkflowTaskStatus = {
  OPEN: "open",
  COMPLETE: "complete",
  DISMISSED: "dismissed",
  MISSED: "missed",
  OVERDUE: "overdue",
  ERROR: "error",
} as const;
export type WorkflowTaskStatusType = TypeOfObjectValues<
  typeof WorkflowTaskStatus
>;

export const UserChoiceTaskMetadataSchema = z.object({
  choice: z.string().min(1).optional(),
});

export type UserChoiceTaskMetadata = z.infer<
  typeof UserChoiceTaskMetadataSchema
>;

export const WorkflowSubmitDatasetTaskMetadataSchema = z.object({
  dataset_submission_ids: z.array(z.string().uuid()).default([]),
});

export type WorkflowSubmitDatasetTaskMetadata = z.infer<
  typeof WorkflowSubmitDatasetTaskMetadataSchema
>;

export const WorkflowTaskBaseSchema = z
  .object({
    name: z.string(),
    type: z.nativeEnum(WorkflowTaskTypes),
    step_id: z.string(),
    step_type: z
      .enum([WorkflowStepType.SYSTEM_ACTION, WorkflowStepType.USER_TASK])
      .optional(),
    status: z.enum([
      WorkflowTaskStatus.OPEN,
      WorkflowTaskStatus.COMPLETE,
      WorkflowTaskStatus.DISMISSED,
      WorkflowTaskStatus.MISSED,
      WorkflowTaskStatus.OVERDUE,
      WorkflowTaskStatus.ERROR,
    ]),
    metadata: UserChoiceTaskMetadataSchema.or(
      WorkflowSubmitDatasetTaskMetadataSchema
    ).or(z.record(z.string(), z.any())),
    description: z.string(),
    is_dismissible: z.boolean(),
    priority_id: z.number().int().min(0).max(2),
    priority_label: WorkflowTaskPriorityLabelSchema,
    assignee_user: z.string().uuid().nullable(),
    assignee_group: z.string().uuid().nullable(),
    restrict_assignee_completion: z.boolean().nullable(),
    workflow_id: z.string(),
    workflow: WorkflowSchema.omit({
      workflow_template_id: true,
      facility_id: true,
    }),
    company_id: z.string(),
    form_submission: z.undefined(),
    emission_event: z
      .array(
        z.object({
          id: z.string().uuid(),
          name: z.string(),
        })
      )
      .optional(),
    entity_id: z.string().uuid().optional(),
    entity_name: z.string().optional(),
    due_date: z.string().optional(),
  })
  .merge(DomainModelSchema)
  .merge(DomainModelMetaSchema)
  .merge(DomainModelMetaExtendedSchema.pick({ updated_by: true }))
  .describe("The domain model schema for a Workflow Task");

export type WorkflowTaskBaseSchema = z.infer<typeof WorkflowTaskBaseSchema>;

export const WorkflowTaskWithFormSubmissionSchema =
  WorkflowTaskBaseSchema.merge(
    z.object({
      config_form_schema: z
        .object({
          id: z.string(),
          name: z.string(),
        })
        .optional(),
      form_submission: z.array(
        z
          .object({
            status: z.enum([
              "validated",
              "invalidated",
              "pending",
              "submitted",
              "draft",
            ]),
            form_schema: z
              .object({
                name: z.string(),
              })
              .merge(DomainModelSchema),
          })
          .merge(DomainModelSchema)
          .merge(DomainModelMetaSchema)
          .merge(DomainModelMetaExtendedSchema)
      ),
    })
  );
export type WorkflowTaskWithFormSubmissionType = z.infer<
  typeof WorkflowTaskWithFormSubmissionSchema
>;

export const WorkflowTaskSchema = z.union([
  WorkflowTaskBaseSchema,
  WorkflowTaskWithFormSubmissionSchema,
]);
export type WorkflowTaskType = z.infer<typeof WorkflowTaskSchema>;

export const WorkflowTaskGetListRequestSchema = createGetListRequestSchema(
  z
    .object({
      id: z.string().uuid(),
      name: z.string(),
      status: z.nativeEnum(WorkflowTaskStatus),
      workflow_id: z.string().uuid(),
      created_at: z.string().datetime(),
      updated_at: z.string().datetime(),
      description: z.string(),
      type: z.nativeEnum(WorkflowTaskTypes),
      assignee_user: z.string().uuid().nullable(),
      assignee_group: z.string().uuid().nullable(),
      distance: z.number().nullable().optional(), // meters
      "assignee_group.user_id": z.string().uuid(),
      "facility.id": z.string().uuid(),
      "facility.name": z.string(),
      "facility.status": z.string(),
      "workflow_template.id": z.string().uuid(),
      "workflow_template.category_id": z.string().uuid(),
      "workflow_template.name": z.string(),
      "workflow_template.description": z.string(),
      "workflow_template.asset_type": AssetTypeSchema,
      "workflow_template.status": z.lazy(() =>
        z.nativeEnum(WorkflowTemplateStatus)
      ),
      "workflow_category.id": z.string().uuid(),
      "workflow_category.name": z.string(),
      "workflow_category.description": z.string(),
      "workflow.facility_id": z.string().uuid(),
      "workflow.due_date": z.string().datetime(),
      "workflow.status": z.nativeEnum(WorkflowStatus),
    })
    .partial()
);
export type WorkflowTaskGetListRequestType = z.infer<
  typeof WorkflowTaskGetListRequestSchema
>;

/**
 * Type predicate to determine if a workflow task is a base workflow task or one
 * with form submissions attached
 * @param task a workflow task
 * @returns true with a type assertion if the workflow task is one with form submissions
 */
export const isWorkflowTaskWithFormSubmission = (
  task: WorkflowTaskType
): task is WorkflowTaskWithFormSubmissionType =>
  task.form_submission !== undefined && Array.isArray(task.form_submission);
